import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import { axiosPrivate } from 'app/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import type { CategoryItem } from './types';

interface CategoryModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: CategoryItem) => void;
  selectedTitles: string[];
}

// Raw shape from GET /v3/organisations/products/categories.
interface ApiCategory {
  categoryId: string;
  name: string;
  productCount?: number;
  publicImageUrl?: string;
}

interface CategoriesResponse {
  categories: ApiCategory[];
}

async function fetchCategories(organisationId: string): Promise<CategoryItem[]> {
  const response = await axiosPrivate.get<CategoriesResponse>(
    '/v3/organisations/products/categories',
    { params: { organisationId } }
  );
  return response.data.categories
    .filter((c) => (c.productCount ?? 0) >= 1)
    .map((c) => ({
      title: c.name,
      imageUrl: c.publicImageUrl,
    }));
}

export function CategoryModal({
  open,
  onClose,
  onAdd,
  selectedTitles,
}: CategoryModalProps): ReactElement {
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['organisation-product-categories', organisationId],
    queryFn: () => fetchCategories(organisationId),
    enabled: open && !!organisationId,
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: 'Failed to load categories' },
  });

  const available = data?.filter((c) => !selectedTitles.includes(c.title));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="vtwo:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Category</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="vtwo:flex vtwo:justify-center vtwo:py-8">
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && isError && (
          <p className="vtwo:text-sm vtwo:text-red-600 vtwo:py-4 vtwo:text-center">
            Unable to load categories. Please try again.
          </p>
        )}

        {!isLoading && !isError && available?.length === 0 && (
          <p className="vtwo:text-sm vtwo:text-gray-500 vtwo:py-4 vtwo:text-center">
            No categories available
          </p>
        )}

        {!isLoading && !isError && available && available.length > 0 && (
          <ul className="vtwo:divide-y vtwo:divide-gray-100 vtwo:max-h-96 vtwo:overflow-y-auto">
            {available.map((category) => (
              <li
                key={category.title}
                className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:py-3 vtwo:px-1"
              >
                <span className="vtwo:text-sm vtwo:font-medium vtwo:text-gray-900 vtwo:truncate vtwo:min-w-0 vtwo:mr-4">
                  {category.title}
                </span>
                {/* description and imageUrl are passed through but not shown */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="vtwo:shrink-0"
                  onClick={() => {
                    onAdd(category);
                    onClose();
                  }}
                >
                  <Plus className="vtwo:h-3 vtwo:w-3" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
